import { useEffect } from 'react';
import type { CSSProperties } from 'react';

// docs/work_log — Shared pagination component. Replaces inline pagers
// scattered across the list pages (ContractsPage / CustomersPage /
// RobotsListPage). Provides:
//   - "start–end / 총 N건" summary
//   - first / prev / next / last buttons (disabled state when at edges)
//   - optional page-size dropdown (20 / 50 / 100 default)
// All styling uses existing design tokens — no new colors.

interface Props {
  page: number; // 1-based
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];
  /**
   * 마감 차수(26-08-24) — 총계 뒤에 붙는 한 마디. **화면이 다시 거른 목록**을 보여 주는
   * 페이지가 "총 N건" 만 말하면 운영자는 목록이 잘렸다고 읽거나 총계를 걸러낸 수로
   * 오해한다(고객사 마스터의 지역/국가 칩). 전체 수를 지우지 않고 걸러낸 수를 **함께**
   * 말하기 위한 자리 — 문장은 부르는 쪽의 정본 한 벌이 만든다(여기서 조립하지 않는다).
   * 안 넘기면 종전 그대로.
   */
  totalSuffix?: string;
}

export function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [20, 50, 100],
  totalSuffix,
}: Props) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Auto-clamp invalid page (e.g. user on page 5 and a filter reduces
  // result count to 1 page). Notifies parent via onPageChange so the
  // canonical page state is corrected upstream.
  useEffect(() => {
    const tp = Math.max(1, Math.ceil(total / pageSize));
    if (page > tp) {
      onPageChange(tp);
    }
  }, [page, total, pageSize, onPageChange]);

  // Defensive: clamp display values so the rendered text never shows
  // nonsense (negative start, page > total pages) on the render
  // tick before the useEffect fires.
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = total > 0 ? (safePage - 1) * pageSize + 1 : 0;
  const end = total > 0 ? Math.min(safePage * pageSize, total) : 0;

  return (
    <div style={wrap}>
      <div style={{ color: 'var(--fg-muted)' }}>
        {total > 0
          ? `${start.toLocaleString()}–${end.toLocaleString()} / 총 ${total.toLocaleString()}건${totalSuffix ?? ''}`
          : '0건'}
      </div>
      {onPageSizeChange && (
        <select
          value={pageSize}
          onChange={(e) => {
            const newSize = Number(e.target.value);
            onPageSizeChange(newSize);
            // Reset to page 1 on page-size change so consumers
            // don't have to remember.
            onPageChange(1);
          }}
          style={selectStyle}
          aria-label="페이지 크기"
        >
          {pageSizeOptions.map((s) => (
            <option key={s} value={s}>
              {s}건/페이지
            </option>
          ))}
        </select>
      )}
      <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
        <button
          type="button"
          disabled={safePage <= 1}
          onClick={() => onPageChange(1)}
          style={btn(safePage <= 1)}
          aria-label="첫 페이지"
          title="첫 페이지"
        >
          <span aria-hidden>«</span>
        </button>
        <button
          type="button"
          disabled={safePage <= 1}
          onClick={() => onPageChange(safePage - 1)}
          style={btn(safePage <= 1)}
        >
          <span aria-hidden style={{ marginRight: 4 }}>
            ‹
          </span>
          이전
        </button>
        <span style={pageIndicator}>
          {safePage} / {totalPages}
        </span>
        <button
          type="button"
          disabled={safePage >= totalPages}
          onClick={() => onPageChange(safePage + 1)}
          style={btn(safePage >= totalPages)}
        >
          다음
          <span aria-hidden style={{ marginLeft: 4 }}>
            ›
          </span>
        </button>
        <button
          type="button"
          disabled={safePage >= totalPages}
          onClick={() => onPageChange(totalPages)}
          style={btn(safePage >= totalPages)}
          aria-label="마지막 페이지"
          title="마지막 페이지"
        >
          <span aria-hidden>»</span>
        </button>
      </div>
    </div>
  );
}

const wrap: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  rowGap: 8,
  flexWrap: 'wrap',
  padding: '8px 12px',
  fontSize: 13,
};

const selectStyle: CSSProperties = {
  height: 28,
  padding: '0 8px',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
  fontSize: 12,
};

const pageIndicator: CSSProperties = {
  padding: '0 12px',
  display: 'inline-flex',
  alignItems: 'center',
  color: 'var(--fg-muted)',
  fontVariantNumeric: 'tabular-nums',
};

function btn(disabled: boolean): CSSProperties {
  return {
    height: 28,
    padding: '0 10px',
    border: '1px solid var(--border-default)',
    borderRadius: 'var(--radius-sm)',
    background: disabled ? 'var(--bg-sunken)' : 'var(--bg-surface)',
    color: disabled ? 'var(--fg-subtle)' : 'var(--fg-default)',
    fontSize: 12,
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
}
